import React, { useState, useRef, useEffect, FC, useCallback } from 'react';

import { ENVS, getEnv } from '@adsk/acs-sc-web-platform-api';
import * as analytics from '@adsk/acs-web-analytics';
import { Account } from '@adsk/alloy-provider';
import Banner from '@adsk/alloy-react-banner';
import { Box, Flex } from '@adsk/alloy-react-box';
import { LinkButton } from '@adsk/alloy-react-button';
import Divider from '@adsk/alloy-react-divider';
import { ICircleIcon } from '@adsk/alloy-react-icon';
import SearchField from '@adsk/alloy-react-search-field';
import { WithAlloySuspense } from '@adsk/alloy-react-suspense';
import Tabs, { Tab } from '@adsk/alloy-react-tabs';
import { Flyout } from '@adsk/alloy-react-tooltip';

import { useTranslation } from '../i18n';
import {
  TABS_NAMES,
  PROJECT_ENTITLEMENTS_LIMIT,
  SEARCH_PLACEHOLDER_KEYS,
} from './consts';
import ProjectsError from './ProjectsError';
import ResultsLoader from './ResultsLoader';
import VirtualizedList from './VirtualizedList';
import { ANALYTICS_DEBOUNCE, ANALYTICS_TYPES } from '../helpers/analytics';
import { useDebounce } from '../hooks/useDebounce';
import { useProjectPickerTabs } from '../hooks/useProjectPickerTabs';

const APS_HUB_LINK_TEXT = 'manage.autodesk.com';
const APS_HELP_LINK =
  'https://help.autodesk.com/view/BUILD/ENU/?guid=Administration';

const APS_HUB_LINK = {
  //STG and QA share URLs
  [ENVS.QA]: 'https://stg-manage.autodesk.com/hubs-list',
  [ENVS.STAGING]: 'https://stg-manage.autodesk.com/hubs-list',
  [ENVS.PRODUCTION]: 'https://manage.autodesk.com/hubs-list',
};

export type ProjectPickerProps = {
  accounts?: Account[];
  currentAccountId?: string;
  currentProjectId?: string;
  currentProductId?: string;
  onClose?: () => void;
  onAccountClick?: (account?: object, url?: string) => void;
  onProjectClick?: (project?: object, url?: string) => void;
};

const ProjectPicker: FC<ProjectPickerProps> = ({
  accounts,
  currentAccountId,
  currentProjectId,
  currentProductId,
  onClose,
  onAccountClick,
  onProjectClick,
}) => {
  const { t } = useTranslation();
  const env = getEnv();
  const searchRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasAccounts = accounts !== undefined;
  const [currentTab, setCurrentTab] = useState<
    (typeof TABS_NAMES)[keyof typeof TABS_NAMES]
  >(hasAccounts ? TABS_NAMES.ACCOUNTS : TABS_NAMES.PROJECTS);
  const [search, setSearch] = useState<string>('');

  const getHelpLinkUrl = () => {
    return APS_HUB_LINK[env];
  };

  const reportSearchOnChangeAnalyticFn = useCallback(
    () => (debounceSearch: string) => {
      if (debounceSearch && debounceSearch !== '') {
        analytics.track(
          ANALYTICS_TYPES.TOPNAVIGATION_PROJECTPICKER_SEARCH_CHANGED,
          {
            product_id: currentProductId,
            searched_text: debounceSearch,
          },
        );
      }
    },
    [currentProductId],
  );

  const debouncedSearch = useDebounce(
    search,
    ANALYTICS_DEBOUNCE,
    reportSearchOnChangeAnalyticFn,
  );

  const onSearchChange = (value: string) => {
    setSearch(value);
  };

  const tabs = useProjectPickerTabs({
    search: debouncedSearch,
    accounts,
    hasAccounts,
    currentTab,
    onClose,
  });

  useEffect(() => searchRef.current?.focus(), []);

  return (
    <Box width="360px" ref={containerRef}>
      <Box padding="16px">
        <SearchField
          ref={searchRef}
          placeholder={t(SEARCH_PLACEHOLDER_KEYS[currentTab])}
          value={search || ''}
          onChange={onSearchChange}
        />
      </Box>

      <Tabs
        labelWidth="full"
        initialTab={currentTab}
        onChange={(tab) => setCurrentTab(tab)}
      >
        {tabs.map(
          (tab) =>
            tab && (
              <Tab
                key={tab.tab}
                tab={tab.tab}
                label={tab.label}
                labelProps={{
                  'data-testid': `ProjectPicker_tab_${tab.tab}`,
                }}
              >
                {tab.tab === TABS_NAMES.ACCOUNTS &&
                  accounts?.some(
                    (account) => account.account_type?.toLowerCase() === 'ems',
                  ) && (
                    <Banner
                      variant={Banner.VARIANTS.WARNING}
                      open={true}
                      inline={true}
                      style={{ width: '100%' }}
                      data-testid="ProjectPicker_APSWarningBanner"
                    >
                      <Flex
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                      >
                        <Flex alignItems="center" justifyContent="flex-start">
                          <Box typography="bodySmall">
                            {t('PROJECT_PICKER_APS_HUBS_WARNING')}
                          </Box>
                          <LinkButton
                            as="a"
                            href={getHelpLinkUrl()}
                            target="_blank"
                            rel="noopener noreferrer"
                            size={LinkButton.SIZES.SMALL}
                            style={{ marginLeft: '4px' }}
                          >
                            {APS_HUB_LINK_TEXT}
                          </LinkButton>
                        </Flex>
                        <Flyout
                          content={
                            <Flex
                              flexDirection="column"
                              justifyContent="flex-start"
                            >
                              <div>{t('APS_HUBS')}</div>
                              <ul style={{ margin: '8px 0' }}>
                                <li>{t('APS_HUBS_DO_NOT_MANAGE')}</li>
                                <li>
                                  <div>
                                    {t('APS_HUB_WARNING')}
                                    <LinkButton
                                      as="a"
                                      href={APS_HELP_LINK}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      size={LinkButton.SIZES.SMALL}
                                      style={{ marginLeft: '4px' }}
                                    >
                                      {APS_HUB_LINK_TEXT}
                                    </LinkButton>
                                  </div>
                                </li>
                              </ul>
                              <Divider />
                              <div>
                                <LinkButton
                                  as="a"
                                  href={APS_HELP_LINK}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  size={LinkButton.SIZES.SMALL}
                                  style={{ marginTop: '12px' }}
                                >
                                  {t('APS_HUB_LEARN_MORE')}
                                </LinkButton>
                              </div>
                            </Flex>
                          }
                          variant={Flyout.VARIANTS.DARK}
                          placement={Flyout.PLACEMENTS.RIGHT}
                          style={{
                            maxWidth: '350px',
                          }}
                        >
                          <ICircleIcon style={{ margin: '0 8px' }} />
                        </Flyout>
                      </Flex>
                    </Banner>
                  )}
                {!tab.error && (
                  <Box
                    typography="heading3"
                    color="charcoal700"
                    padding="8px 16px"
                  >
                    <Flex justifyContent="space-between" alignItems="center">
                      {tab.loading ? (
                        <ResultsLoader />
                      ) : (
                        <div data-testid="ProjectPicker_tab_totalResults">
                          {!!tab.totalResults &&
                            `${tab.totalResults} ${tab.label}`}
                        </div>
                      )}

                      <LinkButton
                        as="a"
                        size={LinkButton.SIZES.MEDIUM}
                        href={tab.viewAllLink}
                        onClick={() => {
                          analytics.track(
                            ANALYTICS_TYPES.TOPNAVIGATION_PROJECTPICKER_VIEWALL_CLICK,
                          );
                        }}
                      >
                        {tab.viewAllLabel}
                      </LinkButton>
                    </Flex>
                  </Box>
                )}

                {debouncedSearch &&
                  !tab.data?.length &&
                  !tab.loading &&
                  !tab.error && (
                    <Box
                      typography="bodySmall"
                      color="charcoal700"
                      textAlign="center"
                      padding="36px 0"
                    >
                      {t('NO_RESULTS_FOUND')}
                    </Box>
                  )}

                {!debouncedSearch &&
                  !tab.data?.length &&
                  !tab.loading &&
                  !tab.error && (
                    <Box
                      typography="bodySmall"
                      color="charcoal700"
                      textAlign="center"
                      padding="36px 0"
                    >
                      {t('EMPTY_RESULTS', { tabName: tab?.label, count: 0 })}
                    </Box>
                  )}

                {!tab.data?.length && tab.loading && tab.renderLoader()}

                {!tab.data?.length && tab.error && (
                  <ProjectsError
                    retry={tab.retry}
                    projectsCount={tab.data?.length}
                    variant={
                      tab.tab === TABS_NAMES.TEMPLATES
                        ? ProjectsError.VARIANTS.TEMPLATES
                        : ProjectsError.VARIANTS.PROJECTS
                    }
                  />
                )}

                {!!tab.data?.length && (
                  <VirtualizedList
                    size={tab.data.length}
                    fetchNextPage={tab.fetchNextPage}
                    isFetchingNextPage={tab.loading}
                    renderLoader={tab.renderLoader}
                    renderItem={(index) =>
                      tab.renderItem({
                        item: tab.data?.[index],
                        search: debouncedSearch,
                        currentAccountId,
                        currentProjectId,
                        currentProductId,
                        onAccountClick,
                        onProjectClick,
                        containerRef,
                      })
                    }
                    hasNextPage={
                      tab.hasNextPage &&
                      tab.data.length >= PROJECT_ENTITLEMENTS_LIMIT
                    }
                    renderError={() =>
                      tab.error ? (
                        <ProjectsError
                          retry={tab.retry}
                          projectsCount={tab.data?.length}
                          variant={
                            tab.tab === TABS_NAMES.TEMPLATES
                              ? ProjectsError.VARIANTS.TEMPLATES
                              : ProjectsError.VARIANTS.PROJECTS
                          }
                        />
                      ) : null
                    }
                  />
                )}
              </Tab>
            ),
        )}
      </Tabs>
    </Box>
  );
};

export default WithAlloySuspense(ProjectPicker);
